<template>
  <div class="frg">
    <!-- MULTI (checkboxes) -->
    <template v-if="allowMultiple">
      <label
        v-for="(opt, i) in stringOptions"
        :key="'m-' + i"
        class="radio-label"
      >
        <input
          type="checkbox"
          :name="name + '[]'"
          :value="opt"
          :checked="proxyArray.includes(opt)"
          :disabled="isReadonly"
          :aria-readonly="isReadonly ? 'true' : 'false'"
          @mousedown.prevent="isReadonly && $event.preventDefault()"
          @change="onToggleMulti(opt, $event.target.checked)"
        />
        {{ opt }}
      </label>
      <div v-if="allowOther" class="other-choice">
        <label class="radio-label">
          <input
            type="checkbox"
            :name="name + '[]'"
            :checked="isOtherSelected"
            :disabled="isReadonly"
            :aria-readonly="isReadonly ? 'true' : 'false'"
            @mousedown.prevent="isReadonly && $event.preventDefault()"
            @change="onToggleOther($event.target.checked)"
          />
          Other
        </label>
        <input
          v-if="isOtherSelected"
          ref="otherInput"
          class="other-input"
          type="text"
          :value="otherText"
          :disabled="isReadonly"
          placeholder="Please specify"
          aria-label="Other answer"
          @input="onOtherInput($event.target.value)"
          @blur="onOtherBlur"
        />
      </div>
    </template>

    <!-- SINGLE (radios) -->
    <template v-else>
      <label
        v-for="(opt, i) in stringOptions"
        :key="'s-' + i"
        class="radio-label"
      >
        <input
          type="radio"
          :name="name"
          :value="opt"
          :checked="!isOtherSelected && proxySingle === opt"
          :disabled="isReadonly"
          :aria-readonly="isReadonly ? 'true' : 'false'"
          @mousedown.prevent="isReadonly && $event.preventDefault()"
          @change="onSelectSingle(opt)"
        />
        {{ opt }}
      </label>
      <div v-if="allowOther" class="other-choice">
        <label class="radio-label">
          <input
            type="radio"
            :name="name"
            :checked="isOtherSelected"
            :disabled="isReadonly"
            :aria-readonly="isReadonly ? 'true' : 'false'"
            @mousedown.prevent="isReadonly && $event.preventDefault()"
            @change="onSelectOther"
          />
          Other
        </label>
        <input
          v-if="isOtherSelected"
          ref="otherInput"
          class="other-input"
          type="text"
          :value="otherText"
          :disabled="isReadonly"
          placeholder="Please specify"
          aria-label="Other answer"
          @input="onOtherInput($event.target.value)"
          @blur="onOtherBlur"
        />
      </div>
    </template>
  </div>
</template>

<script>
import {
  normalizeMultiChoiceValue,
  toggleMultiChoiceValue,
} from "@/utils/dominantChoice";

export default {
  name: "FieldRadioGroup",
  inheritAttrs: false,
  props: {
    modelValue: { type: [String, Number, Array], default: "" },
    options: { type: Array, default: () => [] },
    name: { type: String, default: "radio" },

    // constraints
    readonly: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    defaultValue: { type: [String, Number, Array], default: "" },
    allowMultiple: { type: Boolean, default: false },
    allowOther: { type: Boolean, default: false },
    dominantOptions: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue", "change"],
  data() {
    return {
      otherActive: false,
      otherText: "",
      otherBaseValues: [],
    };
  },
  computed: {
    isReadonly() {
      const attrReadonly =
        this.$attrs.readonly === "" ||
        this.$attrs.readonly === true ||
        this.$attrs.readonly === "true";
      return this.readonly || attrReadonly || this.disabled;
    },
    stringOptions() {
      return this.options.map((o) => (o == null ? "" : String(o)))
        .filter(o => !this.allowOther || o.trim().toLowerCase() !== 'other');
    },
    // single
    proxySingle() {
      return this.modelValue == null ? "" : String(this.modelValue);
    },
    // multi
    proxyArray() {
      const v = this.modelValue;
      if (Array.isArray(v)) return v.map((x) => (x == null ? "" : String(x)));
      // if parent gave scalar while we are in multi mode, coerce to []
      return [];
    },
    customValue() {
      const options = new Set(this.stringOptions);
      if (this.allowMultiple) {
        return this.proxyArray.find((value) => value && !options.has(value)) || "";
      }
      const value = this.proxySingle;
      return value && !options.has(value) ? value : "";
    },
    isOtherSelected() {
      return this.allowOther && (this.otherActive || !!this.customValue);
    },
  },
  mounted() {
    this.syncOtherFromValue();
    this.initFromDefaults();
  },
  watch: {
    options() {
      // keep current value(s) valid when options change
      const opts = this.stringOptions;

      if (this.allowMultiple) {
        this.emitInitializedSelection(this.normalizeMulti(this.proxyArray));
      } else {
        const cur = this.proxySingle;
        if (!opts.includes(cur) && !(this.allowOther && cur)) {
          const dv = this.pickSingleDefault();
          const next = dv && opts.includes(dv) ? dv : "";
          this.$emit("update:modelValue", next);
          this.$emit("change", next);
        }
      }
    },
    allowMultiple() {
      // when the mode flips, coerce the model shape and apply defaults if needed
      this.initFromDefaults(true);
    },
    dominantOptions() {
      if (!this.allowMultiple) return;
      this.emitInitializedSelection(this.normalizeMulti(this.proxyArray));
    },
    modelValue: {
      deep: true,
      handler() {
        this.syncOtherFromValue();
        if (!this.allowMultiple) return;
        this.emitInitializedSelection(this.normalizeMulti(this.proxyArray));
      },
    },
    allowOther(enabled) {
      if (enabled) {
        this.syncOtherFromValue();
        return;
      }
      if (!this.otherActive && !this.customValue) return;
      this.otherActive = false;
      this.otherText = "";
      const next = this.allowMultiple
        ? this.proxyArray.filter((value) => this.stringOptions.includes(value))
        : this.stringOptions.includes(this.proxySingle) ? this.proxySingle : "";
      this.emitInitializedSelection(next, true);
    },
  },
  methods: {
    // ----- defaults / init -----
    pickSingleDefault() {
      // defaultValue may be scalar or array; for single, pick first valid
      const opts = this.stringOptions;
      const dv = this.defaultValue;

      if (Array.isArray(dv)) {
        const first = dv.map((x) => String(x)).find((x) => opts.includes(x));
        return first || "";
      }
      const s = dv == null ? "" : String(dv);
      return opts.includes(s) ? s : "";
    },
    pickMultiDefault() {
      // intersect defaults array with options
      const opts = this.stringOptions;
      const dv = this.defaultValue;

      if (Array.isArray(dv)) {
        return this.normalizeMulti(
          dv.map((x) => String(x)).filter((x) => opts.includes(x))
        );
      }
      const s = dv == null ? "" : String(dv);
      return this.normalizeMulti(opts.includes(s) ? [s] : []);
    },
    normalizeMulti(value) {
      return normalizeMultiChoiceValue(
        value,
        this.stringOptions,
        this.dominantOptions,
        this.allowOther
      );
    },
    syncOtherFromValue() {
      const custom = this.customValue;
      if (!custom) {
        const values = this.allowMultiple ? this.proxyArray : [this.proxySingle];
        if (this.otherText && !values.includes(this.otherText)) {
          this.otherActive = false;
          this.otherText = "";
        }
        return;
      }
      this.otherActive = true;
      this.otherText = custom;
      if (this.allowMultiple) {
        this.otherBaseValues = this.proxyArray.filter(value => this.stringOptions.includes(value));
      }
    },
    selectionEqualsCurrent(next) {
      if (this.allowMultiple) {
        const current = this.proxyArray;
        return (
          Array.isArray(next) &&
          current.length === next.length &&
          current.every((value, index) => value === next[index])
        );
      }
      return this.proxySingle === String(next ?? "");
    },
    emitInitializedSelection(next, force = false) {
      if (!force && this.selectionEqualsCurrent(next)) return;
      this.$emit("update:modelValue", next);
      this.$emit("change", next);
    },
    initFromDefaults(force = false) {
      const hasValue = (v) =>
        (Array.isArray(v) ? v.length > 0 : v !== null && String(v).trim() !== "");

      if (this.allowMultiple) {
        const cur = this.normalizeMulti(this.proxyArray);
        if (!this.selectionEqualsCurrent(cur)) {
          this.emitInitializedSelection(cur);
          return;
        }
        if (!hasValue(cur) || force) {
          const next = this.pickMultiDefault();
          this.emitInitializedSelection(next, force);
        }
      } else {
        const cur = this.proxySingle;
        if (!hasValue(cur) || force) {
          const next = this.pickSingleDefault();
          this.emitInitializedSelection(next, force);
        }
      }
    },

    // ----- interactions -----
    onSelectSingle(val) {
      if (this.isReadonly) return;
      this.otherActive = false;
      this.otherText = "";
      this.$emit("update:modelValue", val);
      this.$emit("change", val);
    },
    onToggleMulti(opt, checked) {
      if (this.isReadonly) return;
      const next = toggleMultiChoiceValue({
        value: this.proxyArray,
        option: opt,
        checked,
        options: this.stringOptions,
        dominantOptions: this.dominantOptions,
        allowOther: this.allowOther,
      });
      this.otherBaseValues = next.filter(value => this.stringOptions.includes(value));
      if (!next.some((value) => !this.stringOptions.includes(value))) {
        if (this.dominantOptions.includes(opt) && checked) {
          this.otherActive = false;
          this.otherText = "";
        }
      }
      this.$emit("update:modelValue", next);
      this.$emit("change", next); //
    },
    focusOtherInput() {
      this.$nextTick(() => {
        const input = this.$refs.otherInput;
        if (input && typeof input.focus === "function") input.focus();
      });
    },
    onSelectOther() {
      if (this.isReadonly) return;
      this.otherActive = true;
      this.$emit("update:modelValue", this.otherText);
      this.$emit("change", this.otherText);
      this.focusOtherInput();
    },
    onToggleOther(checked) {
      if (this.isReadonly) return;
      this.otherActive = checked;
      const regularValues = this.proxyArray.filter((value) =>
        this.stringOptions.includes(value)
      );
      const dominant = new Set(this.dominantOptions);
      const next = checked
        ? [
            ...regularValues.filter((value) => !dominant.has(value)),
            ...(this.otherText ? [this.otherText] : []),
          ]
        : regularValues;
      this.otherBaseValues = checked
        ? regularValues.filter(value => !dominant.has(value))
        : regularValues;
      if (!checked) this.otherText = "";
      this.$emit("update:modelValue", next);
      this.$emit("change", next);
      if (checked) this.focusOtherInput();
    },
    onOtherInput(value) {
      if (this.isReadonly) return;
      this.otherActive = true;
      this.otherText = value;
      const next = this.allowMultiple
        ? Array.from(new Set([
            ...this.otherBaseValues,
            ...(value ? [value] : []),
          ]))
        : value;
      this.$emit("update:modelValue", next);
      this.$emit("change", next);
    },
    onOtherBlur() {
      if (this.isReadonly) return;
      const trimmed = this.otherText.trim();
      if (this.stringOptions.includes(trimmed)) {
        this.onOtherInput(trimmed);
        this.otherActive = false;
        this.otherText = "";
        return;
      }
      if (trimmed === this.otherText) return;
      this.onOtherInput(trimmed);
    },
  },
};
</script>

<style scoped>
.frg {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.2;
}
.other-choice {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.other-input {
  width: min(calc(100% - 28px), 28rem);
  box-sizing: border-box;
  margin-left: 28px;
  padding: 7px 9px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font: inherit;
}
.other-input:focus {
  border-color: #2563eb;
  outline: 2px solid rgba(37, 99, 235, 0.15);
}

/* Make radios & checkboxes look identical (circular dot) */
.radio-label input[type="radio"],
.radio-label input[type="checkbox"] {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  width: 18px;
  height: 18px;
  margin: 0;

  border: 2px solid #9ca3af;          /* gray-400 */
  border-radius: 50%;                  /* always circle */
  background: #ffffff;
  position: relative;
  cursor: pointer;
  display: inline-block;

  transition: border-color .2s ease, background-color .2s ease, box-shadow .2s ease;
}

.radio-label input[type="radio"]:hover,
.radio-label input[type="checkbox"]:hover {
  border-color: #6b7280;               /* gray-500 */
}

.radio-label input[type="radio"]:focus,
.radio-label input[type="checkbox"]:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(107, 114, 128, 0.25); /* gray focus ring */
}

/* Checked state: dark circle with white inner dot */
.radio-label input[type="radio"]:checked,
.radio-label input[type="checkbox"]:checked {
  background-color: #111827;           /* gray-900 */
  border-color: #111827;
}
.radio-label input[type="radio"]:checked::after,
.radio-label input[type="checkbox"]:checked::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  margin-top: -4px;
  margin-left: -4px;
  background: #ffffff;
  border-radius: 50%;
}

/* Disabled / readonly visual */
.radio-label input[type="radio"]:disabled,
.radio-label input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
